import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ShieldCheckIcon, CreditCardIcon, GiftIcon, LockIcon, FingerprintIcon, SmartphoneIcon, HelpCircleIcon, MessageCircleIcon, AlertTriangleIcon, FileTextIcon, InfoIcon, LogOutIcon, ChevronRightIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface ProfileProps {
  navigate: (screen: string) => void;
}
export function Profile({
  navigate
}: ProfileProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const menuGroups = [{
    title: 'Account',
    items: [{
      icon: UserIcon,
      label: 'Edit Profile',
      action: () => {}
    }, {
      icon: ShieldCheckIcon,
      label: 'KYC Verification',
      action: () => navigate('kyc'),
      badge: 'Tier 2'
    }, {
      icon: CreditCardIcon,
      label: 'Bank Accounts',
      action: () => {}
    }, {
      icon: GiftIcon,
      label: 'Referral Program',
      action: () => {}
    }]
  }, {
    title: 'Security',
    items: [{
      icon: LockIcon,
      label: 'Change PIN',
      action: () => {}
    }, {
      icon: FingerprintIcon,
      label: 'Biometric Login',
      action: () => {},
      toggle: true
    }, {
      icon: SmartphoneIcon,
      label: 'Two-Factor Auth',
      action: () => {}
    }]
  }, {
    title: 'Support',
    items: [{
      icon: HelpCircleIcon,
      label: 'Help Center',
      action: () => {}
    }, {
      icon: MessageCircleIcon,
      label: 'Live Chat',
      action: () => {}
    }, {
      icon: AlertTriangleIcon,
      label: 'Report a Problem',
      action: () => {}
    }]
  }, {
    title: 'About',
    items: [{
      icon: FileTextIcon,
      label: 'Terms & Conditions',
      action: () => {}
    }, {
      icon: ShieldCheckIcon,
      label: 'Privacy Policy',
      action: () => {}
    }, {
      icon: InfoIcon,
      label: 'App Version',
      action: () => {},
      value: '2.1.0'
    }]
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50" data-id="element-812">
      <TopBar title="Profile" onNotificationClick={() => navigate('notifications')} data-id="element-813" />

      <div className="px-5 pt-4 pb-6" data-id="element-814">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-6 text-center relative overflow-hidden" data-id="element-815">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#1B3B36]/5 to-transparent" data-id="element-816"></div>

          <div className="w-20 h-20 rounded-full bg-[#1B3B36] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4 relative z-10 shadow-lg shadow-emerald-900/20 border-4 border-white" data-id="element-817">
            AO
          </div>

          <h2 className="text-xl font-bold text-[#1B3B36] mb-1" data-id="element-818">
            Adaeze Okonkwo
          </h2>
          <p className="text-sm text-gray-500 mb-1" data-id="element-819">adaeze@email.com</p>
          <p className="text-sm text-gray-500 font-medium" data-id="element-820">+234 801 234 5678</p>
        </div>

        {/* KYC Status */}
        <div onClick={() => navigate('kyc')} className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white mb-8 shadow-lg shadow-emerald-500/20 cursor-pointer active:scale-95 transition-transform relative overflow-hidden" data-id="element-821">
          <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-822"></div>

          <div className="flex justify-between items-center relative z-10" data-id="element-823">
            <div data-id="element-824">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold mb-2 border border-white/20" data-id="element-825">
                <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-826" /> Tier 2 Verified
              </div>
              <p className="text-sm text-emerald-50 font-medium" data-id="element-827">
                BVN ✓ • ID ✓ • Address ⏳
              </p>
            </div>
            <div className="text-right" data-id="element-828">
              <span className="text-xs font-bold bg-white text-emerald-600 px-3 py-1.5 rounded-lg shadow-sm" data-id="element-829">
                Upgrade
              </span>
            </div>
          </div>
        </div>

        {/* Menu Groups */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-6" data-id="element-830">
          {menuGroups.map((group, gIdx) => <motion.div variants={itemVariants} key={gIdx} data-id="element-831">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2" data-id="element-832">
                {group.title}
              </h3>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-833">
                {group.items.map((item, iIdx) => <button key={iIdx} onClick={item.action} className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${iIdx !== group.items.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-834">
                    <div className="flex items-center gap-3" data-id="element-835">
                      <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500" data-id="element-836">
                        <item.icon className="w-4 h-4" data-id="element-837" />
                      </div>
                      <span className="text-sm font-bold text-[#1B3B36]" data-id="element-838">
                        {item.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-2" data-id="element-839">
                      {item.badge && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-md uppercase tracking-wider" data-id="element-840">
                          {item.badge}
                        </span>}
                      {item.value && <span className="text-xs font-medium text-gray-400" data-id="element-841">
                          {item.value}
                        </span>}
                      {item.toggle ? <div className="w-10 h-6 bg-emerald-500 rounded-full relative shadow-inner" data-id="element-842">
                          <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm" data-id="element-843"></div>
                        </div> : <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-844" />}
                    </div>
                  </button>)}
              </div>
            </motion.div>)}

          {/* Logout */}
          <motion.div variants={itemVariants} data-id="element-845">
            <button className="w-full bg-white rounded-2xl border border-rose-100 p-4 flex items-center justify-center gap-2 text-rose-500 font-bold shadow-sm hover:bg-rose-50 transition-colors active:scale-95" data-id="element-846">
              <LogOutIcon className="w-5 h-5" data-id="element-847" /> Log Out
            </button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>;
}